import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Redirect, useHistory, useLocation } from 'react-router-dom';
import { Route, RouteProps } from '@modernhealth/web-analytics';
import * as authSelectors from 'src/modules/auth/selectors/authenticationSelectors';
import { CenteredSpinner } from 'src/shared/components/CenteredSpinner';
import { useIsAppInitialized } from 'src/modules/auth/containers/AppContext';
import { UnauthenticatedLayout } from 'src/shared/layout/UnauthenticatedLayout';
import { AuthenticatedLayout } from 'src/shared/layout/AuthenticatedLayout';
import { NoLayout } from 'src/shared/layout/NoLayout';
import type { Dispatch } from 'src/config/rootStore';

interface Props extends RouteProps {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    component: React.ComponentType<any>;
    secured?: boolean;
    redirectTo?: string;
    /**
     * If the user is authenticated, redirect them to `/dashboard`
     * when they visit this page.
     */
    unauthenticatedOnly?: boolean;
    /**
     * Render the route with no layout (e.g. no header, no footer)
     */
    noLayout?: boolean;
    additionalProps?: Record<string, unknown>;
}

const RedirectUnauthenticated: React.FC<{
    redirectTo: string;
}> = ({ redirectTo }) => {
    const dispatch = useDispatch<Dispatch>();
    const location = useLocation();
    const history = useHistory();

    useEffect(() => {
        dispatch.authentication.setRedirectFrom(location.pathname);

        history.push(redirectTo);
    }, [location.pathname, dispatch.authentication, redirectTo, history]);

    return null;
};

const SecuredRoute: React.FC<{
    secured: boolean;
    redirectTo?: string;
    unauthenticatedOnly?: boolean;
    noLayout?: boolean;
}> = ({ secured, children, redirectTo = '/', unauthenticatedOnly, noLayout }) => {
    const isAuthenticated = useSelector(authSelectors.isAuthenticated);
    const isAuthenticating = useSelector(authSelectors.isAuthenticating);

    const isAppInitialized = useIsAppInitialized();

    if (isAuthenticating) {
        return <CenteredSpinner />;
    }

    if (isAuthenticated && !isAppInitialized) {
        return <CenteredSpinner />;
    }

    if (isAuthenticated && unauthenticatedOnly) {
        return <Redirect to="/dashboard" />;
    }

    if (!secured) {
        if (noLayout) {
            return <NoLayout>{children}</NoLayout>;
        }

        return <UnauthenticatedLayout>{children}</UnauthenticatedLayout>;
    }

    if (!isAuthenticated) {
        return <RedirectUnauthenticated redirectTo={redirectTo} />;
    }

    if (noLayout) {
        return <NoLayout>{children}</NoLayout>;
    }

    return <AuthenticatedLayout>{children}</AuthenticatedLayout>;
};

export const RouteGuard: React.FC<Props> = ({
    component: Component,
    secured = false,
    redirectTo,
    unauthenticatedOnly,
    additionalProps = {},
    noLayout = false,
    ...routeProps
}) => {
    return (
        <Route
            {...routeProps}
            render={props => (
                <SecuredRoute
                    secured={secured}
                    redirectTo={redirectTo}
                    unauthenticatedOnly={unauthenticatedOnly}
                    noLayout={noLayout}
                >
                    <Component {...props} {...additionalProps} />
                </SecuredRoute>
            )}
        />
    );
};
